'use client';

import {
  type Dispatch,
  type SetStateAction,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';

interface IClipSwiperContext {
  count: number;
  total: number;
  index: number;
  dragStartY: React.RefObject<number | null>;
  draggedRef: React.RefObject<boolean>;
  viewportRef: React.RefObject<HTMLDivElement | null>;
  step: (direction: number) => void;
  onTap: () => void;
  setDragPx: Dispatch<SetStateAction<number>>;
  setWithTransition: Dispatch<SetStateAction<boolean>>;
  setIndex: Dispatch<SetStateAction<number>>;
}

interface IClipSwiperHandlers {
  onPointerDown: (event: React.PointerEvent<HTMLDivElement>) => void;
  onPointerMove: (event: React.PointerEvent<HTMLDivElement>) => void;
  onPointerUp: (event: React.PointerEvent<HTMLDivElement>) => void;
  onClickCapture: (event: React.MouseEvent<HTMLDivElement>) => void;
  onTransitionEnd: () => void;
}

interface IClipSwiperController {
  index: number;
  dragPx: number;
  withTransition: boolean;
  total: number;
  activeRealIndex: number;
  viewportRef: React.RefObject<HTMLDivElement | null>;
  step: (direction: number) => void;
  handlers: IClipSwiperHandlers;
}

const SWIPE_THRESHOLD_RATIO = 0.16;
const DRAG_CLICK_GUARD_PX = 10;
const WHEEL_DELTA_MIN = 12;
const WHEEL_LOCK_MS = 500;

function clampIndex(value: number, total: number): number {
  return Math.min(Math.max(value, 0), total - 1);
}

function resolveWrap(ctx: IClipSwiperContext): void {
  const target = ctx.index === ctx.total - 1 ? 1 : ctx.index === 0 ? ctx.count : null;
  if (target === null) {
    return;
  }
  ctx.setWithTransition(false);
  ctx.setIndex(target);
  requestAnimationFrame(() => requestAnimationFrame(() => ctx.setWithTransition(true)));
}

function beginDrag(ctx: IClipSwiperContext, event: React.PointerEvent<HTMLDivElement>): void {
  ctx.dragStartY.current = event.clientY;
  ctx.draggedRef.current = false;
  ctx.setWithTransition(false);
  ctx.viewportRef.current?.setPointerCapture(event.pointerId);
}

function moveDrag(ctx: IClipSwiperContext, event: React.PointerEvent<HTMLDivElement>): void {
  if (ctx.dragStartY.current === null) {
    return;
  }
  const delta = event.clientY - ctx.dragStartY.current;
  if (Math.abs(delta) > DRAG_CLICK_GUARD_PX) {
    ctx.draggedRef.current = true;
  }
  ctx.setDragPx(delta);
}

function endDrag(ctx: IClipSwiperContext, event: React.PointerEvent<HTMLDivElement>): void {
  if (ctx.dragStartY.current === null) {
    return;
  }
  const delta = event.clientY - ctx.dragStartY.current;
  ctx.dragStartY.current = null;
  ctx.viewportRef.current?.releasePointerCapture(event.pointerId);
  const threshold = (ctx.viewportRef.current?.clientHeight ?? 1) * SWIPE_THRESHOLD_RATIO;
  ctx.setWithTransition(true);
  ctx.setDragPx(0);
  if (delta <= -threshold) {
    ctx.step(1);
  } else if (delta >= threshold) {
    ctx.step(-1);
  } else if (!ctx.draggedRef.current) {
    ctx.onTap();
  }
}

function guardClick(
  draggedRef: React.RefObject<boolean>,
  event: React.MouseEvent<HTMLDivElement>,
): void {
  if (!draggedRef.current) {
    return;
  }
  event.preventDefault();
  event.stopPropagation();
  draggedRef.current = false;
}

function bindWheel(el: HTMLDivElement | null, step: (direction: number) => void): () => void {
  if (!el) {
    return () => undefined;
  }
  let locked = false;
  const handleWheel = (event: WheelEvent): void => {
    event.preventDefault();
    if (locked || Math.abs(event.deltaY) < WHEEL_DELTA_MIN) {
      return;
    }
    locked = true;
    step(event.deltaY > 0 ? 1 : -1);
    window.setTimeout(() => {
      locked = false;
    }, WHEEL_LOCK_MS);
  };
  el.addEventListener('wheel', handleWheel, { passive: false });
  return () => el.removeEventListener('wheel', handleWheel);
}

export function useClipSwiper(
  count: number,
  onTap: () => void,
  onStep: () => void,
): IClipSwiperController {
  const total = count + 2;
  const viewportRef = useRef<HTMLDivElement>(null);
  const dragStartY = useRef<number | null>(null);
  const draggedRef = useRef(false);
  const [index, setIndex] = useState(1);
  const [dragPx, setDragPx] = useState(0);
  const [withTransition, setWithTransition] = useState(true);

  const step = useCallback(
    (direction: number) => {
      setWithTransition(true);
      setIndex((current) => clampIndex(current + direction, total));
      onStep();
    },
    [total, onStep],
  );

  useEffect(() => bindWheel(viewportRef.current, step), [step]);

  const ctx: IClipSwiperContext = {
    count,
    total,
    index,
    dragStartY,
    draggedRef,
    viewportRef,
    step,
    onTap,
    setDragPx,
    setWithTransition,
    setIndex,
  };

  return {
    index,
    dragPx,
    withTransition,
    total,
    activeRealIndex: (index - 1 + count) % count,
    viewportRef,
    step,
    handlers: {
      onPointerDown: (event) => beginDrag(ctx, event),
      onPointerMove: (event) => moveDrag(ctx, event),
      onPointerUp: (event) => endDrag(ctx, event),
      onClickCapture: (event) => guardClick(draggedRef, event),
      onTransitionEnd: () => resolveWrap(ctx),
    },
  };
}
